import { VocabularyWord } from "./worksheet/conjugationData";

// Plain Unicode range check for CJK Unified Ideographs, mirroring the exact
// pattern isKanaChar already uses in worksheet/conjugationLayout.ts (kana's
// 0x3040-0x30ff range) - deliberately not isKnownKanji from
// worksheet/characters.ts, which is scoped to only this app's curated
// N5-N1 "known kanji" dataset (used for worksheet stroke-data coverage) and
// would wrongly treat any kanji outside that list as "not kanji".
export function isKanjiCharacter(char: string): boolean {
    const code = char.codePointAt(0) ?? 0;
    return code >= 0x4e00 && code <= 0x9fff;
}

export interface KanjiGroup {
    kanji: string;
    words: VocabularyWord[];
}

export interface VocabularyKanjiGroups {
    groups: KanjiGroup[];
    noKanji: VocabularyWord[];
}

// Groups vocabulary words by every distinct kanji character they contain -
// a word is filed under each kanji it has (e.g. 食事 appears in both the 食
// group and the 事 group), but at most once per group even if that kanji
// repeats within the word (e.g. 時々, 人々 - in practice stored with the
// iteration mark 々 rather than a literal repeat, but a per-word Set of
// unique kanji characters handles either case). Words carry over by
// reference, unchanged. Pure-kana words go into a separate noKanji bucket.
// Group order follows first appearance of each kanji across the input
// array, so the UI order is stable rather than re-sorted by frequency or
// codepoint.
export function groupWordsByKanji(words: VocabularyWord[]): VocabularyKanjiGroups {
    const groupsByKanji = new Map<string, VocabularyWord[]>();
    const noKanji: VocabularyWord[] = [];

    for (const word of words) {
        const kanjiInWord = new Set<string>();
        for (const char of word.word) {
            if (isKanjiCharacter(char)) kanjiInWord.add(char);
        }

        if (kanjiInWord.size === 0) {
            noKanji.push(word);
            continue;
        }

        for (const kanji of kanjiInWord) {
            if (!groupsByKanji.has(kanji)) groupsByKanji.set(kanji, []);
            groupsByKanji.get(kanji)?.push(word);
        }
    }

    const groups: KanjiGroup[] = Array.from(groupsByKanji.entries()).map(([kanji, groupWords]) => ({
        kanji,
        words: groupWords,
    }));

    return { groups, noKanji };
}
